/**
 * Everything said about ONE sync run — written once, shown in two places.
 *
 * The desktop inline panel and the mobile drawer are two wrappers around this
 * file. Neither owns a field list of its own: a detail surface that exists twice
 * is a surface where the two copies drift, and the reader has no way to know
 * which one is stale.
 *
 * READ-ONLY, AND STRUCTURALLY SO. There is no "retry this run" control, because
 * there is no run-level retry: the backend re-syncs a SCOPE, never a past run,
 * and a button implying otherwise would promise an operation that does not
 * exist. The workspace-level manual sync stays where it already lives.
 *
 * WHAT IS ABSENT IS ABSENT ON PURPOSE. `bexio_sync_runs` stores no trigger, no
 * initiator, no retry counter, no API-call count and no per-item error tally, so
 * none of those appears here. The design reference shows several of them; a
 * plausible number with nothing behind it is worse than a missing row.
 *
 * NO WATERMARKS EITHER. `BexioSyncRunRead` deliberately keeps the incremental
 * watermark columns server-internal, so a watermark section could never render
 * against the real API — only against a fixture that invented the fields. It was
 * removed in SYNC-3b rather than given a wire contract it was never meant to have.
 */
import React from "react";

import {
  DrawerField,
  DrawerSection,
} from "../../../components/admin/bexio/workspace/BexioDetailDrawer";
import { formatDateTime } from "../../../utils/datetime";
import {
  fmtCount,
  formatDurationSeconds,
  runDurationSeconds,
  scopeCategoryBadge,
  scopeLabel,
  statusLabel,
} from "./bexioSyncRunsView";
import BexioSyncScopeIcon from "./BexioSyncScopeIcon";
import SyncStatusPill from "./BexioSyncStatusPill";

/**
 * The one thing said about a failed run.
 *
 * The raw `error` column is server-internal and never reaches the wire — the
 * read schema exposes only a coarse `error_category` derived from the status. So
 * this states that the run failed and says, plainly, that the cause is not shown
 * here. Inventing a likely reason from the category would be the browser
 * narrating something it was deliberately not told.
 */
function ErrorNote({ category }) {
  if (!category) return null;
  return (
    <section className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm dark:border-red-500/30 dark:bg-red-500/10">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-red-700 dark:text-red-300">
        Fehler
      </h3>
      <p className="mt-2 text-sm text-red-700 dark:text-red-300">
        Synchronisationsfehler
      </p>
      <p className="mt-1 text-xs text-red-700/80 dark:text-red-300/80">
        Die technische Fehlerursache wird in dieser Ansicht nicht offengelegt.
      </p>
    </section>
  );
}

/**
 * The three counters, side by side.
 *
 * Still a description list, so each number keeps the label that gives it
 * meaning. `upserted_count` is inserts AND updates together — the writer records
 * no breakdown, so splitting it into "Neu" and "Aktualisiert" would be
 * invention. Nothing is summed and nothing is subtracted either: "fetched minus
 * upserted minus unchanged" looks like an error count and is not one.
 */
function CountRow({ run }) {
  const cells = [
    ["Geholt", run.fetched_count],
    ["Gespeichert", run.upserted_count],
    ["Unverändert", run.unchanged_count],
  ];
  return (
    <section className="rounded-lg border border-border p-4">
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Verarbeitung
      </h3>
      <dl className="grid grid-cols-3 gap-2">
        {cells.map(([label, value]) => (
          <div
            key={label}
            className="min-w-0 rounded-lg border border-border bg-muted/40 px-3 py-2"
          >
            <dt className="truncate text-xs text-muted-foreground">{label}</dt>
            <dd className="mt-0.5 text-lg font-semibold tabular-nums text-foreground">
              {fmtCount(value)}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * Scope, status and id — the three things that identify which run this is.
 *
 * Shared by both surfaces so the drawer and the panel introduce a run with the
 * same words. The icon is decoration; the German scope label beside it is the
 * name.
 */
function DetailHeader({ run }) {
  const category = scopeCategoryBadge(run.entity_scope);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <BexioSyncScopeIcon scope={run.entity_scope} />
      <span className="min-w-0 truncate text-sm font-medium text-foreground">
        {scopeLabel(run.entity_scope)}
      </span>
      {category ? (
        <span className="shrink-0 whitespace-nowrap rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          {category}
        </span>
      ) : null}
      <span className="ml-auto shrink-0">
        <SyncStatusPill status={run.status} />
      </span>
    </div>
  );
}

export default function BexioSyncRunDetailContent({ run }) {
  if (!run) return null;

  const duration = runDurationSeconds(run);
  const stillRunning = !run.finished_at;

  return (
    <>
      <DetailHeader run={run} />

      <DrawerSection title="Lauf">
        <DrawerField label="Start" value={formatDateTime(run.started_at)} />
        {/* "Läuft" rather than an em dash: the run has no end BECAUSE it is still
            going, which is a different fact from a missing value. */}
        <DrawerField
          label="Ende"
          value={stillRunning ? "Läuft" : formatDateTime(run.finished_at)}
        />
        <DrawerField
          label="Dauer"
          value={stillRunning ? "Läuft" : formatDurationSeconds(duration)}
          mono
        />
        {/* Full id, wrapped rather than truncated: it is what an operator quotes
            when asking about a specific run, so it has to be selectable in full. */}
        <DrawerField label="Lauf-ID" value={run.id} wide mono />
      </DrawerSection>

      <CountRow run={run} />

      {scopeCategoryBadge(run.entity_scope) ? (
        <p className="text-xs text-muted-foreground">
          Die Zähler beziehen sich auf den jeweiligen Sync-Umfang.
        </p>
      ) : null}

      <ErrorNote category={run.error_category} />
    </>
  );
}
